import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { firstValueFrom } from 'rxjs';

import { TwcPopupComponent } from '@topcon/twc-angular';

import { JobSiteService, ToastService, TranslationService } from '@mirage-projects/cw-commons';
import { JobSite } from '@mirage-projects/cw-models';

type DeleteJobSiteForm = FormGroup<{
    removeChildProjects: FormControl<boolean>;
    removeLayers: FormControl<boolean>;
    removeGCPFiles: FormControl<boolean>;
    removeDesignDataFiles: FormControl<boolean>;
}>;

type DeleteFormControlKeys = keyof DeleteJobSiteForm['controls'];
type DeleteJobSiteValues = Record<DeleteFormControlKeys, boolean>;

@Component({
    selector: 'cw-delete-jobsite-dialog',
    templateUrl: './delete-job-site-dialog.component.html',
    standalone: false,
})
export class DeleteJobSiteDialogComponent implements OnInit {
    @Input() jobSite!: JobSite;

    @ViewChild('popup') popup!: TwcPopupComponent;

    deleteJobSiteForm!: DeleteJobSiteForm;
    isLoading = false;

    readonly deleteJobSiteLabels: Record<DeleteFormControlKeys, string> = {
        removeChildProjects: 'JOB_SITE.DELETE.CHILD_PROJECTS',
        removeLayers: 'JOB_SITE.DELETE.LAYERS',
        removeGCPFiles: 'JOB_SITE.DELETE.GCP',
        removeDesignDataFiles: 'JOB_SITE.DELETE.DESIGN',
    };

    readonly childItems: Array<DeleteFormControlKeys> = ['removeLayers'];

    constructor(
        private jobSiteService: JobSiteService,
        private toastService: ToastService,
        private translationService: TranslationService
    ) {}

    ngOnInit(): void {
        this.deleteJobSiteForm = new FormGroup({
            removeChildProjects: new FormControl<boolean>({ value: false, disabled: true }, { nonNullable: true }),
            removeLayers: new FormControl<boolean>({ value: false, disabled: true }, { nonNullable: true }),
            removeGCPFiles: new FormControl<boolean>({ value: false, disabled: true }, { nonNullable: true }),
            removeDesignDataFiles: new FormControl<boolean>({ value: false, disabled: true }, { nonNullable: true }),
        });

        this.setFormValues();
        this.updateFormStatus();

        this.deleteJobSiteForm.valueChanges.subscribe(() => this.updateFormStatus());
    }

    /**
     * Call delete job service
     */
    async deleteJobSite(): Promise<void> {
        try {
            this.isLoading = true;
            await firstValueFrom(this.jobSiteService.deleteJobSite(this.jobSite.id, this.deleteJobSiteForm.value));
            this.popup.onClose();
            this.showMessage(false);
        } catch (error) {
            this.showMessage(true);
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Show success or error message
     * @param isError Show error message if true
     */
    showMessage(isError: boolean): void {
        const action = isError ? 'error' : 'generic';
        const msg = isError ? 'JOB_SITE.DELETE.ERROR' : 'JOB_SITE.DELETE.SUCCESS';
        const params = { jobSite: this.jobSite.name };
        this.toastService[action](this.translationService.translate(msg, params));
    }

    /**
     * If the form item is a child item (to add extra indentation)
     * @param key
     * @returns
     */
    isChildItem(key: DeleteFormControlKeys): boolean {
        return this.childItems.includes(key);
    }

    /**
     * Get the property keys of the form controls.
     */
    get formOptionKeys(): Array<DeleteFormControlKeys> {
        return Object.keys(this.deleteJobSiteForm.controls) as Array<DeleteFormControlKeys>;
    }

    private updateFormStatus(): void {
        const enabledStatus = this.getEnabledStatus();
        this.formOptionKeys.forEach((key) => {
            const action = enabledStatus[key] ? 'enable' : 'disable';
            this.deleteJobSiteForm.controls[key][action]({ emitEvent: false });
        });
    }

    private setFormValues(): void {
        // The enabled ones are selected by default
        const defaultValues = this.getEnabledStatus();
        defaultValues.removeLayers = defaultValues.removeChildProjects;

        this.deleteJobSiteForm.setValue(defaultValues);
    }

    private getEnabledStatus(): DeleteJobSiteValues {
        return {
            removeChildProjects: !!this.jobSite.projects?.length,
            removeLayers: this.deleteJobSiteForm.controls.removeChildProjects.value,
            removeGCPFiles: !!this.jobSite.controlPointsFiles?.length,
            removeDesignDataFiles: !!this.jobSite.designDataFiles?.length,
        };
    }
}
